import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Archive, RotateCcw, Users, GraduationCap, Calendar, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { batchApi, studentApi, classApi } from '@/lib/api';

interface ArchivedBatch {
  id: string;
  year: string;
  displayName: string;
  startDate: string;
  endDate: string;
  archivedAt: string;
  archivedBy: string;
  isArchived: boolean;
}

interface ArchivedStudent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  studentId: string;
  batchId: string;
  batchYear?: string;
  archivedAt: string;
  archivedBy: string;
  isArchived: boolean;
}

interface ArchivedClass {
  id: string;
  name: string;
  grade: string;
  section: string;
  batchId: string;
  batchYear?: string;
  studentCount: number;
  archivedAt: string;
  archivedBy: string;
  isArchived: boolean;
}

const ArchivePage = () => {
  const { toast } = useToast();
  const [archivedBatches, setArchivedBatches] = useState<ArchivedBatch[]>([]);
  const [archivedStudents, setArchivedStudents] = useState<ArchivedStudent[]>([]);
  const [archivedClasses, setArchivedClasses] = useState<ArchivedClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState<{type: string, id: string, name: string} | null>(null);

  useEffect(() => {
    loadArchivedData();
  }, []);

  const loadArchivedData = async () => {
    setLoading(true);
    try {
      // Load archived batches
      const batchesResponse = await batchApi.getArchived();
      setArchivedBatches(batchesResponse.content || []);

      // Load archived students
      const studentsResponse = await studentApi.getArchived();
      setArchivedStudents(studentsResponse.content || []);

      // Load archived classes
      const classesResponse = await classApi.getArchived();
      setArchivedClasses(classesResponse.content || []);

    } catch (error) {
      console.error('Error loading archived data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load archived data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreItem = (type: string, id: string, name: string) => {
    setItemToRestore({ type, id, name });
    setRestoreDialogOpen(true);
  };

  const confirmRestore = async () => {
    if (!itemToRestore) return;

    setRestoring(itemToRestore.id);
    try {
      switch (itemToRestore.type) {
        case 'batch':
          await batchApi.restore(itemToRestore.id);
          break;
        case 'student':
          await studentApi.restore(itemToRestore.id);
          break;
        case 'class':
          await classApi.restore(itemToRestore.id);
          break;
      }

      toast({
        title: 'Success',
        description: `${itemToRestore.name} has been restored successfully`,
      });

      // Reload data
      await loadArchivedData();
    } catch (error) {
      console.error('Error restoring item:', error);
      toast({
        title: 'Error',
        description: `Failed to restore ${itemToRestore.name}`,
        variant: 'destructive',
      });
    } finally {
      setRestoring(null);
      setRestoreDialogOpen(false);
      setItemToRestore(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Archive Management</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading archived data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Archive Management</h1>
        <Button onClick={loadArchivedData} variant="outline">
          <RotateCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Archived Batches</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{archivedBatches.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Archived Students</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{archivedStudents.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Archived Classes</CardTitle>
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{archivedClasses.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Archived Data Tabs */}
      <Tabs defaultValue="batches" className="space-y-4">
        <TabsList>
          <TabsTrigger value="batches">Archived Batches</TabsTrigger>
          <TabsTrigger value="students">Archived Students</TabsTrigger>
          <TabsTrigger value="classes">Archived Classes</TabsTrigger>
        </TabsList>

        <TabsContent value="batches" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Archived Batches</CardTitle>
            </CardHeader>
            <CardContent>
              {archivedBatches.length === 0 ? (
                <div className="text-center py-8">
                  <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No archived batches found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch Year</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Archived Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedBatches.map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-medium">{batch.year}</TableCell>
                        <TableCell>{batch.displayName}</TableCell>
                        <TableCell>
                          {new Date(batch.startDate).toLocaleDateString()} - {new Date(batch.endDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{new Date(batch.archivedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreItem('batch', batch.id, batch.year)}
                            disabled={restoring === batch.id}
                          >
                            {restoring === batch.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                            <span className="ml-2">Restore</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="students" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Archived Students</CardTitle>
            </CardHeader>
            <CardContent>
              {archivedStudents.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No archived students found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Archived Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">{student.studentId}</TableCell>
                        <TableCell>{student.firstName} {student.lastName}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{student.batchYear || 'Unknown'}</Badge>
                        </TableCell>
                        <TableCell>{new Date(student.archivedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreItem('student', student.id, `${student.firstName} ${student.lastName}`)}
                            disabled={restoring === student.id}
                          >
                            {restoring === student.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                            <span className="ml-2">Restore</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="classes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Archived Classes</CardTitle>
            </CardHeader>
            <CardContent>
              {archivedClasses.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No archived classes found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Class Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Archived Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedClasses.map((classItem) => (
                      <TableRow key={classItem.id}>
                        <TableCell className="font-medium">{classItem.name}</TableCell>
                        <TableCell>{classItem.grade}</TableCell>
                        <TableCell>{classItem.section}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{classItem.batchYear || 'Unknown'}</Badge>
                        </TableCell>
                        <TableCell>{classItem.studentCount}</TableCell>
                        <TableCell>{new Date(classItem.archivedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreItem('class', classItem.id, classItem.name)}
                            disabled={restoring === classItem.id}
                          >
                            {restoring === classItem.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                            <span className="ml-2">Restore</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore {itemToRestore?.type}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to restore "{itemToRestore?.name}"? This will make it active again and visible in the regular views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivePage;
